import { useEffect, useState } from "react";
import { loadStripe } from "@stripe/stripe-js";
import { Elements, CardElement, useStripe, useElements } from "@stripe/react-stripe-js";
import { CreditCard, Loader2, ShieldCheck, AlertCircle } from "lucide-react";
import { toast } from "sonner";
import api from "@/lib/api";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";

let stripePromise = null;
const getStripe = () => {
  if (!stripePromise) stripePromise = api.get("/admin/stripe/publishable-key").then((r) => loadStripe(r.data.publishable_key));
  return stripePromise;
};

const CARD_STYLE = { style: { base: { color: "#fff", fontSize: "15px", fontFamily: "system-ui, sans-serif", "::placeholder": { color: "#6b7280" }, iconColor: "#f59e0b" }, invalid: { color: "#f87171", iconColor: "#f87171" } }, hidePostalCode: false, disableLink: true };

const ChargeForm = ({ user, onClose }) => {
  const stripe = useStripe();
  const elements = useElements();
  const [amount, setAmount] = useState("");
  const [description, setDescription] = useState("");
  const [name, setName] = useState(user.name || "");
  const [complete, setComplete] = useState(false);
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);

  const charge = async () => {
    setBusy(true); setError("");
    try {
      const { paymentMethod, error: pmErr } = await stripe.createPaymentMethod({ type: "card", card: elements.getElement(CardElement), billing_details: { name: name || undefined, email: user.email || undefined } });
      if (pmErr) { setError(pmErr.message); return; }
      const r = await api.post(`/admin/users/${user.user_id}/charge`, { amount: Number(amount), description, payment_method_id: paymentMethod.id });
      toast.success(`Charged $${r.data.amount.toFixed(2)} to ${r.data.church || user.email}`, { description: `Paid invoice ${r.data.number || ""} added to their billing page` });
      onClose();
    } catch (e) {
      const msg = e.response?.data?.detail || "The charge didn't go through — please try again";
      setError(msg); toast.error("Card was not charged", { description: msg });
    } finally { setBusy(false); }
  };

  return (
    <>
      <div className="space-y-4 pt-1">
        <div className="grid grid-cols-[1fr_2fr] gap-3">
          <div className="space-y-1.5"><Label className="text-gray-300 text-xs">Amount (USD)</Label><Input data-testid="charge-amount" type="number" min="1" step="0.01" value={amount} onChange={(e) => setAmount(e.target.value)} placeholder="0.00" className="bg-black/30 border-gray-800" /></div>
          <div className="space-y-1.5"><Label className="text-gray-300 text-xs">Description</Label><Input data-testid="charge-description" value={description} onChange={(e) => setDescription(e.target.value)} maxLength={500} placeholder="e.g. Camera setup — phone payment" className="bg-black/30 border-gray-800" /></div>
        </div>
        <div className="space-y-1.5"><Label className="text-gray-300 text-xs">Name on card</Label><Input data-testid="charge-card-name" value={name} onChange={(e) => setName(e.target.value)} className="bg-black/30 border-gray-800" /></div>
        <div className="space-y-1.5">
          <Label className="text-gray-300 text-xs">Card number, expiry, CVV and ZIP</Label>
          <div data-testid="charge-card-element" className="rounded-md border border-gray-800 bg-black/30 px-3 py-3 focus-within:border-amber-500/60 transition-colors">
            <CardElement options={CARD_STYLE} onChange={(e) => { setComplete(e.complete); setError(e.error?.message || ""); }} />
          </div>
          <p className="text-[11px] text-gray-500 flex items-center gap-1.5"><ShieldCheck className="h-3.5 w-3.5 text-emerald-400" /> Card details go straight to Stripe and are never stored by the portal.</p>
        </div>
        {error && <p data-testid="charge-error" className="flex items-start gap-2 rounded-lg border border-red-500/30 bg-red-500/10 px-3 py-2.5 text-sm text-red-300"><AlertCircle className="h-4 w-4 mt-0.5 shrink-0" />{error}</p>}
      </div>
      <div className="mt-4 flex justify-end gap-3">
        <button onClick={onClose} className="rounded-lg px-4 py-2 text-sm text-gray-400 hover:text-white">Cancel</button>
        <button data-testid="charge-submit" onClick={charge} disabled={busy || !stripe || !complete || !(Number(amount) >= 1) || !description.trim()} className="inline-flex items-center gap-2 rounded-lg gold-gradient px-4 py-2 text-sm font-semibold text-black hover:brightness-110 disabled:opacity-50">
          {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <CreditCard className="h-4 w-4" />} {Number(amount) >= 1 ? `Charge $${Number(amount).toFixed(2)}` : "Charge card"}
        </button>
      </div>
    </>
  );
};

export const ChargeClientDialog = ({ user, onClose }) => {
  const [stripe, setStripe] = useState(null);
  const [loadErr, setLoadErr] = useState("");
  useEffect(() => { getStripe().then(setStripe).catch((e) => setLoadErr(e.response?.data?.detail || "Card payments aren't available right now")); }, []);
  return (
    <Dialog open onOpenChange={(o) => { if (!o) onClose(); }}>
      <DialogContent data-testid="charge-dialog" className="bg-[#0E131F] border-gray-800 text-white sm:max-w-[500px]">
        <DialogHeader>
          <DialogTitle className="font-display text-xl flex items-center gap-2"><CreditCard className="h-5 w-5 text-amber-400" /> Charge Client</DialogTitle>
          <DialogDescription className="text-gray-400 text-sm">Take a card payment over the phone for <span className="text-gray-200 font-medium">{user.organization || user.name || user.email}</span>. A paid invoice is added to their billing page and Stripe emails them a receipt.</DialogDescription>
        </DialogHeader>
        {loadErr ? <p className="text-sm text-red-300">{loadErr}</p> : !stripe ? <div className="py-8 flex justify-center"><Loader2 className="h-5 w-5 animate-spin text-amber-400" /></div> : (
          <Elements stripe={stripe}><ChargeForm user={user} onClose={onClose} /></Elements>
        )}
      </DialogContent>
    </Dialog>
  );
};
